/**
 * Hook för OrganizationChart
 * Hanterar state och logik för organisationshierarki-visualisering
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  useReactFlow,
} from 'reactflow';
import dagre from 'dagre';
import { toast } from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import { useAuth } from '@/store/authStore';
import { organizationsService } from '@/services/organizations/organizationsService';
import type { Organization } from '@/types/organizations';
import {
  OrganizationNodeData,
  DAGRE_SETTINGS,
  DEFAULT_EDGE_STYLE,
  createEdge,
  createNewOrganizationNode,
} from '@/types/organizationChart';
import {
  isUnresolvedOrganizationParent,
  orderNewOrganizationNodes,
  organizationSaveErrorMessage,
} from '@/utils/organizationChartSave';

const toIdString = (value: string | number | undefined | null): string =>
  value === undefined || value === null ? '' : String(value);

// Dagre layout function
const getLayoutedElements = (nodes: Node[], edges: Edge[], direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: DAGRE_SETTINGS.nodesep,
    ranksep: DAGRE_SETTINGS.ranksep,
    marginx: DAGRE_SETTINGS.marginx,
    marginy: DAGRE_SETTINGS.marginy,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: DAGRE_SETTINGS.nodeWidth,
      height: DAGRE_SETTINGS.nodeHeight,
    });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - DAGRE_SETTINGS.nodeWidth / 2,
        y: nodeWithPosition.y - DAGRE_SETTINGS.nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

export function useOrganizationChart() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { fitView } = useReactFlow();

  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Data state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [topNodes, setTopNodes] = useState<Organization[]>([]);
  const [selectedTopNode, setSelectedTopNode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNodeName, setNewNodeName] = useState('');
  const [newNodeDescription, setNewNodeDescription] = useState('');
  const [newNodeParent, setNewNodeParent] = useState<string>('');

  // Load organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoading(true);
        const orgs = await organizationsService.getAllOrganizations();
        setOrganizations(orgs);

        const tops = orgs.filter((o) => !o.parent_id);
        setTopNodes(tops.sort((a, b) => a.name.localeCompare(b.name, 'sv')));

        const userOrgId = user?.organization_id ? String(user.organization_id) : null;
        if (userOrgId && orgs.some((o) => toIdString(o.id) === userOrgId)) {
          setSelectedTopNode(userOrgId);
        } else if (tops.length === 1) {
          setSelectedTopNode(toIdString(tops[0].id));
        } else if (tops.length > 1 && userOrgId) {
          const directTopNode = tops.find((t) => toIdString(t.id) === userOrgId);
          if (directTopNode) {
            setSelectedTopNode(toIdString(directTopNode.id));
          }
        }
      } catch {
        toast.error(t('organizations.loadError'));
      } finally {
        setLoading(false);
      }
    };
    loadOrganizations();
  }, [t, user]);

  // Build tree when top node changes
  const buildOrganizationTree = useCallback(() => {
    const topOrg = organizations.find((o) => toIdString(o.id) === toIdString(selectedTopNode));
    if (!topOrg) return;

    const childrenMap = new Map<string, Organization[]>();
    organizations.forEach((org) => {
      if (org.parent_id) {
        const parentId = toIdString(org.parent_id);
        if (!childrenMap.has(parentId)) {
          childrenMap.set(parentId, []);
        }
        childrenMap.get(parentId)!.push(org);
      }
    });

    const newNodes: Node<OrganizationNodeData>[] = [];
    const newEdges: Edge[] = [];

    const createNodesRecursive = (org: Organization) => {
      const nodeId = toIdString(org.id);

      newNodes.push({
        id: nodeId,
        type: 'organization',
        position: { x: 0, y: 0 },
        data: {
          label: org.name,
          description: org.description,
          isTopNode: toIdString(org.id) === toIdString(selectedTopNode),
          organizationId: toIdString(org.id),
        },
      });

      const children = childrenMap.get(toIdString(org.id)) || [];
      children.forEach((child) => {
        newEdges.push(createEdge(nodeId, toIdString(child.id)));
        createNodesRecursive(child);
      });
    };

    createNodesRecursive(topOrg);

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      newNodes,
      newEdges
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);

    setTimeout(() => fitView({ padding: 0.2 }), 100);
  }, [organizations, selectedTopNode, setNodes, setEdges, fitView]);

  useEffect(() => {
    if (!selectedTopNode || organizations.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }
    buildOrganizationTree();
  }, [selectedTopNode, organizations, buildOrganizationTree, setNodes, setEdges]);

  // Auto-layout
  const handleAutoLayout = useCallback(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes,
      edges
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setTimeout(() => fitView({ padding: 0.2 }), 100);
    toast.success(t('copy.hooks.layoutUpdated'));
  }, [nodes, edges, setNodes, setEdges, fitView, t]);

  // Handle connections
  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            ...DEFAULT_EDGE_STYLE,
          },
          eds
        )
      );
      setHasChanges(true);
      toast.success(t('copy.hooks.linkCreated'));
    },
    [setEdges, t]
  );

  // Handle node click
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  // Handle right click on node: re-root tree from clicked node
  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault();
    setSelectedTopNode(node.id);
    setSelectedNode(node);
    toast.success(t('copy.hooks.showingHierarchy', { name: String(node.data?.label ?? node.id) }));
  }, [t]);

  // Open add modal
  const openAddModal = useCallback(
    (parentId?: string) => {
      setNewNodeParent(parentId || selectedTopNode);
      setShowAddModal(true);
    },
    [selectedTopNode]
  );

  const closeAddModal = useCallback(() => {
    setShowAddModal(false);
    setNewNodeName('');
    setNewNodeDescription('');
    setNewNodeParent('');
  }, []);

  // Add new node
  const handleAddNode = useCallback(() => {
    if (!newNodeName.trim()) {
      toast.error(t('copy.hooks.organizationNameRequired'));
      return;
    }

    const parentNode = nodes.find((n) => n.id === newNodeParent);
    const newNodeId = `temp-${Date.now()}`;

    const newNode = createNewOrganizationNode(
      newNodeId,
      newNodeName,
      newNodeDescription,
      {
        x: parentNode ? parentNode.position.x : 200,
        y: parentNode ? parentNode.position.y + 120 : 200,
      }
    );

    const newEdge = createEdge(newNodeParent, newNodeId);

    setNodes((nds) => [...nds, newNode]);
    setEdges((eds) => [...eds, newEdge]);
    closeAddModal();
    setHasChanges(true);

    // Run auto-layout
    setTimeout(() => {
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        [...nodes, newNode],
        [...edges, newEdge]
      );
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    }, 50);

    toast.success(
      t('copy.hooks.orgAddedUnder', {
        name: newNodeName,
        parent: parentNode?.data.label || t('copy.hooks.topNode'),
      })
    );
  }, [
    newNodeName,
    newNodeDescription,
    newNodeParent,
    nodes,
    edges,
    setNodes,
    setEdges,
    closeAddModal,
    fitView,
    t,
  ]);

  // Delete selected node
  const handleDeleteNode = useCallback(() => {
    if (!selectedNode) return;

    if (selectedNode.data.isTopNode) {
      toast.error(t('copy.hooks.cannotDeleteTopNode'));
      return;
    }

    setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
    setEdges((eds) =>
      eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id)
    );
    setSelectedNode(null);
    setHasChanges(true);
    toast.success(t('copy.hooks.nodeDeleted'));
  }, [selectedNode, setNodes, setEdges, t]);

  // Save changes
  const handleSave = useCallback(async () => {
    try {
      const parentMap = new Map<string, string>();
      edges.forEach((e) => {
        parentMap.set(e.target, e.source);
      });

      const createdIds = new Map<string, string>();
      const ordered = orderNewOrganizationNodes(
        nodes.filter((node) => node.data.isNew),
        parentMap
      );

      for (const node of ordered) {
        const rawParent = parentMap.get(node.id);
        const parentId =
          (rawParent ? createdIds.get(rawParent) : undefined) || rawParent || selectedTopNode;
        const created = await organizationsService.createOrganization({
          name: node.data.label,
          description: node.data.description,
          parent_id: parentId,
          is_top_node: false,
        });
        createdIds.set(node.id, toIdString(created.id));
      }

      toast.success(t('copy.hooks.structureSaved'));
      setHasChanges(false);

      const orgs = await organizationsService.getAllOrganizations();
      setOrganizations(orgs);
    } catch (err: unknown) {
      if (isUnresolvedOrganizationParent(err)) {
        toast.error(t('copy.hooks.orgSaveUnresolvedParent'));
        return;
      }
      toast.error(t('copy.hooks.orgSaveFailed', { message: organizationSaveErrorMessage(err) }));
    }
  }, [nodes, edges, selectedTopNode, t]);

  // Excel import
  const handleExcelImport = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const lines = text.split('\n').filter((l) => l.trim());
        const dataLines = lines.slice(1);

        const importedNodes: Node<OrganizationNodeData>[] = [];
        const importedEdges: Edge[] = [];

        dataLines.forEach((line, index) => {
          const [name, description] = line
            .split(/[,;\t]/)
            .map((s) => s.trim().replace(/"/g, ''));

          if (name) {
            const newNodeId = `import-${Date.now()}-${index}`;
            importedNodes.push(
              createNewOrganizationNode(newNodeId, name, description || '', { x: 0, y: 0 })
            );
            importedEdges.push(createEdge(selectedTopNode, newNodeId));
          }
        });

        if (importedNodes.length > 0) {
          const allNodes = [...nodes, ...importedNodes];
          const allEdges = [...edges, ...importedEdges];

          const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
            allNodes,
            allEdges
          );
          setNodes(layoutedNodes);
          setEdges(layoutedEdges);

          setHasChanges(true);
          toast.success(
            t('copy.hooks.importedOrganizations', { count: importedNodes.length })
          );

          setTimeout(() => fitView({ padding: 0.2 }), 100);
        } else {
          toast.error(t('copy.hooks.noOrganizationsInFile'));
        }
      } catch {
        toast.error(t('organizations.importError'));
      }

      event.target.value = '';
    },
    [nodes, edges, selectedTopNode, setNodes, setEdges, fitView, t]
  );

  return {
    // Refs
    reactFlowWrapper,

    // ReactFlow state
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onNodeClick,
    onNodeContextMenu,

    // Data
    topNodes,
    selectedTopNode,
    selectedNode,
    loading,
    hasChanges,

    // Modal state
    showAddModal,
    newNodeName,
    newNodeDescription,
    newNodeParent,

    // Setters
    setSelectedTopNode,
    setNewNodeName,
    setNewNodeDescription,
    setNewNodeParent,

    // Actions
    handleAutoLayout,
    openAddModal,
    closeAddModal,
    handleAddNode,
    handleDeleteNode,
    handleSave,
    handleExcelImport,
  };
}
